import { useState } from "react";
import { MessageCircle, ShieldCheck, Clock } from "lucide-react";
import { sendWhatsAppOtp, verifyWhatsAppOtp } from "../../services/api";
import { WHATSAPP_OTP } from "../../constants/testIds/auth";
import { authInput, authLabel, authPrimaryBtn, authSecondaryBtn, formatDetail } from "./AuthShell";

const SOON = "WhatsApp verification will be available soon.";

// Provider-ready OTP UI. The backend keeps OTP disabled until a provider is
// connected, so this surfaces a friendly coming-soon notice instead.
export const WhatsAppOtpPanel = ({ phone, onPhoneChange, onVerified, compact = false }) => {
  const [code, setCode] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");

  const run = async (action, fn) => {
    setBusy(action);
    setNotice("");
    try {
      const data = await fn();
      if (action === "verify" && onVerified) onVerified(data);
      else setNotice("Code sent. Check WhatsApp.");
    } catch (err) {
      const status = err?.response?.status;
      setNotice(status === 503 ? SOON : formatDetail(err?.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-4" data-testid={WHATSAPP_OTP.panel}>
      {!compact && (
        <div>
          <label htmlFor="wa-otp-phone" className={authLabel}>
            WhatsApp Number
          </label>
          <div className="mt-2 flex">
            <span className="flex items-center border border-r-0 border-brand-sand bg-brand-cream px-4 text-sm text-brand-stone">+91</span>
            <input
              id="wa-otp-phone"
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={(e) => onPhoneChange(e.target.value.replace(/[^\d\s+-]/g, ""))}
              placeholder="10-digit number"
              data-testid={WHATSAPP_OTP.phoneInput}
              className={`${authInput} mt-0`}
            />
          </div>
        </div>
      )}
      <button
        type="button"
        disabled={busy !== "" || !phone}
        onClick={() => run("send", () => sendWhatsAppOtp(phone))}
        data-testid={WHATSAPP_OTP.sendButton}
        className={authSecondaryBtn}
      >
        <MessageCircle className="h-4 w-4" />
        {busy === "send" ? "Sending…" : compact ? "Send WhatsApp Code" : "Send Code"}
      </button>
      <div>
        <label htmlFor="wa-otp-code" className={authLabel}>
          Verification Code
        </label>
        <input
          id="wa-otp-code"
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="• • • • • •"
          data-testid={WHATSAPP_OTP.codeInput}
          className={`${authInput} text-center font-serif text-2xl tracking-[0.5em]`}
        />
      </div>
      <button
        type="button"
        disabled={busy !== "" || code.length < 4 || !phone}
        onClick={() => run("verify", () => verifyWhatsAppOtp(phone, code))}
        data-testid={WHATSAPP_OTP.verifyButton}
        className={authPrimaryBtn}
      >
        <ShieldCheck className="h-4 w-4" />
        {busy === "verify" ? "Verifying…" : "Verify Code"}
      </button>
      {notice && (
        <p
          className="flex items-start gap-2 border border-brand-gold/40 bg-brand-champagne px-4 py-3 text-xs leading-relaxed text-brand-charcoal"
          data-testid={WHATSAPP_OTP.notice}
          role="status"
        >
          <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-gold" />
          {notice}
        </p>
      )}
    </div>
  );
};
